/**
 * Cómo se lee un informe de auditoría de nómina.
 *
 * La lógica vive aquí y no en el componente por la misma razón que `avisoTimbres.js`: dentro
 * del render no se puede probar, y dos mutaciones sobrevivieron la última vez que se intentó
 * —el primer ciclo devuelve `null` antes de que llegue la respuesta, así que un test que sólo
 * mire el DOM pasa aunque la lógica esté rota—.
 */

/** Las severidades del contrato, de menor a mayor. */
export const SEVERIDADES = ['INFO', 'LOW', 'MEDIUM', 'HIGH', 'BLOCKER']

/**
 * Lo que bloquea el timbrado, del `PAYROLL_AUDIT_CONTRACT.json`.
 *
 * **`WARNING` no está**: es una diferencia que vale reportar pero que no impide emitir. Ser
 * más estricto que el contrato también es apartarse de él.
 */
export const RESULTADOS_QUE_BLOQUEAN = ['FAIL', 'REVIEW_REQUIRED', 'CONFLICT']

/** Cómo se pinta cada resultado. El color dice la gravedad antes de leer el texto. */
export const ESTILO_RESULTADO = {
  PASS: { variant: 'success', texto: 'Sin diferencias' },
  WARNING: { variant: 'warning', texto: 'Con advertencias' },
  FAIL: { variant: 'danger', texto: 'Diferencias detectadas' },
  REVIEW_REQUIRED: { variant: 'warning', texto: 'Requiere revisión' },
  CONFLICT: { variant: 'danger', texto: 'Conflicto normativo' },
}

export const ESTILO_SEVERIDAD = {
  INFO: 'secondary',
  LOW: 'info',
  MEDIUM: 'warning',
  HIGH: 'warning',
  BLOCKER: 'danger',
}

/**
 * La distinción que el Gate H del contrato exige en el informe:
 *
 * > *«report distinguishes ERROR DETECTED from REVIEW REQUIRED»*
 *
 * No es cosmética. **Un error detectado es un número que no cuadra**: alguien calculó mal.
 * **Una revisión requerida es un dato que falta**: nadie calculó mal, falta información para
 * poder afirmar. Mezclarlos haría que un contador persiguiera un error que no existe, o que
 * ignorara uno que sí.
 */
export function claseDeHallazgo(hallazgo) {
  if (!hallazgo) return null
  if (hallazgo.outcome === 'FAIL' || hallazgo.code === 'DIFERENCIA_DE_VALOR') {
    return 'ERROR_DETECTADO'
  }
  if (hallazgo.outcome === 'CONFLICT') return 'CONFLICTO'
  return 'REQUIERE_REVISION'
}

/** Cuántos hay de cada clase, para el encabezado del informe. */
export function resumirHallazgos(hallazgos) {
  const base = { ERROR_DETECTADO: 0, REQUIERE_REVISION: 0, CONFLICTO: 0, total: 0 }
  if (!Array.isArray(hallazgos)) return base
  return hallazgos.reduce((acc, h) => {
    const clase = claseDeHallazgo(h)
    if (clase) acc[clase] += 1
    acc.total += 1
    return acc
  }, base)
}

/** Si este informe impide emitir el CFDI. */
export function bloqueaTimbrado(auditoria) {
  if (!auditoria) return false
  if (auditoria.blocks_stamping !== undefined) return Boolean(auditoria.blocks_stamping)
  return RESULTADOS_QUE_BLOQUEAN.includes(auditoria.outcome)
}

/**
 * Los hallazgos ordenados para leerse: lo que bloquea primero.
 *
 * Dentro de cada clase, por severidad descendente. Un informe con 200 hallazgos donde el
 * importante está en la fila 150 es un informe que nadie lee.
 */
export function ordenarParaLeer(hallazgos) {
  const peso = { ERROR_DETECTADO: 0, CONFLICTO: 1, REQUIERE_REVISION: 2 }
  return [...(hallazgos || [])].sort((a, b) => {
    const pa = peso[claseDeHallazgo(a)] ?? 3
    const pb = peso[claseDeHallazgo(b)] ?? 3
    if (pa !== pb) return pa - pb
    return SEVERIDADES.indexOf(b.severity) - SEVERIDADES.indexOf(a.severity)
  })
}

/**
 * Cómo se muestra un valor del informe.
 *
 * Los estados de nulidad **no son montos y no deben parecerlo**: si `UNKNOWN` se pintara como
 * `0.00`, el contador leería que el trabajador no tuvo horas extra cuando lo que pasa es que
 * nadie lo dijo. Es la misma distinción que la ingesta defiende, llevada a la pantalla.
 */
export const ESTADOS_SIN_VALOR = {
  UNKNOWN: 'no se sabe',
  NOT_PROVIDED: 'no lo mandó la fuente',
  NOT_APPLICABLE: 'no aplica',
  DISPUTED: 'el dato no cuadra',
  INSUFFICIENT_EVIDENCE: 'evidencia insuficiente',
  REVIEW_REQUIRED: 'requiere revisión',
}

export function esEstadoSinValor(valor) {
  return typeof valor === 'string' && valor in ESTADOS_SIN_VALOR
}

export function describirValor(valor) {
  if (valor === null || valor === undefined) return '—'
  if (esEstadoSinValor(valor)) return ESTADOS_SIN_VALOR[valor]
  return String(valor)
}
